import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDownRight, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const SLIDES = [
  {
    id: "devata",
    eyebrow: "Devata.org · The Sacred Carvings",
    title: ["Sacred Carvings,", "Forgotten Kings,", "Living Words."],
    note: "a living archive.",
    description:
      "Two decades of scholarship on the goddesses of Angkor Wat — the most complete census of a vanished feminine civilization.",
    image:
      "https://images.unsplash.com/photo-1602642977157-b7c8b8003afd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjYW1ib2RpYSUyMGtobWVyJTIwdGVtcGxlJTIwYW5na29yJTIwd2F0JTIwc3VucmlzZXxlbnwwfHx8fDE3OTEzMTI3MzV8MA&ixlib=rb-4.1.0&q=85",
    cta: [
      { to: "/writings", label: "Enter the Archive", icon: ArrowDownRight, primary: false },
      { to: "/books", label: "Browse the Books", icon: BookOpen, primary: true },
    ],
  },
  {
    id: "datasia",
    eyebrow: "DatAsia Editions · Rare Khmer Histories",
    title: ["Rare Chronicles,", "Royal Dancers,", "Printed Memory."],
    note: "six masterworks.",
    description:
      "Six landmark volumes of Southeast Asian history — chronicles, translations and travel classics, meticulously edited for a new century of readers.",
    image:
      "https://images.unsplash.com/photo-1565326378721-74186198f882?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxBbmdrb3IlMjBXYXQlMjBhcHNhcmElMjBkZXZhdGElMjBzdG9uZSUyMGNhcnZpbmd8ZW58MHx8fHwxNzkxMzEyODU4fDA&ixlib=rb-4.1.0&q=85",
    cta: [
      { to: "/books", label: "Explore the Library", icon: BookOpen, primary: true },
      { to: "/about", label: "About the Press", icon: ArrowDownRight, primary: false },
    ],
  },
  {
    id: "ballet",
    eyebrow: "Field Notes · Royal Cambodian Dance",
    title: ["Thousand-Year", "Gestures of the", "Royal Ballet."],
    note: "preserved in silver & light.",
    description:
      "From Groslier's 1927 photographs to the UNESCO-endorsed Sacred Dancers — the vocabulary of a thousand-year-old court, held in living hands.",
    image:
      "https://images.unsplash.com/photo-1730440983959-8d4bf2afe5f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxDYW1ib2RpYW4lMjBjbGFzc2ljYWwlMjBraG1lciUyMGRhbmNlcnxlbnwwfHx8fDE3OTEzMTI4NTh8MA&ixlib=rb-4.1.0&q=85",
    cta: [
      { to: "/writings", label: "Read the Dispatches", icon: ArrowDownRight, primary: true },
      { to: "/books", label: "Buy a Volume", icon: BookOpen, primary: false },
    ],
  },
];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: {
    y: "-115%",
    transition: { duration: 0.5, ease: [0.65, 0, 0.35, 1] },
  },
};

export const HeroCarousel = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const timer = useRef(null);
  const paused = useRef(false);

  const go = (next, direction = 1) => {
    setDir(direction);
    setIdx((next + SLIDES.length) % SLIDES.length);
  };

  useEffect(() => {
    const tick = () => {
      if (!paused.current) go(idx + 1, 1);
    };
    timer.current = setTimeout(tick, 6500);
    return () => clearTimeout(timer.current);
  }, [idx]);

  const slide = SLIDES[idx];

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-[var(--ink)]"
    >
      {/* image stack */}
      <AnimatePresence initial={false} mode="sync">
        <motion.div
          key={slide.id}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1.02 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 z-0"
        >
          <img
            src={slide.image}
            alt={slide.eyebrow}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[var(--ink)]/60" />
          <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-[var(--ink)]/80 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-[85%] bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/80 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[var(--bg)] to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* copy */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-20 lg:pb-28 pt-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.32em] text-[#F2E4C0]"
            >
              {slide.eyebrow}
            </motion.span>

            <h1
              data-testid="hero-main-title"
              className="mt-6 font-serif text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight text-[#F9F6F0]"
            >
              {slide.title.map((line, i) => (
                <span key={`${slide.id}-${i}`} className="line-mask">
                  <motion.span
                    custom={i}
                    variants={lineVariant}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="block"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-8 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between"
            >
              <p className="max-w-md font-sans text-base sm:text-lg text-[#EDE4D2] leading-relaxed">
                {slide.description}
                <span className="font-hand text-2xl text-[#F0D48A] ml-2 -rotate-2 inline-block">
                  {slide.note}
                </span>
              </p>

              <div className="flex flex-wrap items-center gap-4">
                {slide.cta.map((c) => (
                  <Link
                    key={c.label}
                    to={c.to}
                    data-testid={`hero-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm font-semibold transition-colors duration-300 ${
                      c.primary
                        ? "bg-[var(--gold)] text-[var(--ink)] hover:bg-[#E8C877]"
                        : "border border-[#F9F6F0]/50 text-[#F9F6F0] hover:bg-[#F9F6F0] hover:text-[var(--ink)]"
                    }`}
                  >
                    {c.primary && <c.icon className="h-4 w-4" />}
                    {c.label}
                    {!c.primary && (
                      <c.icon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
                    )}
                  </Link>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* controls */}
        <div className="mt-14 flex items-end justify-between border-t border-[#F9F6F0]/15 pt-6">
          <div className="flex items-center gap-5">
            <button
              onClick={() => go(idx - 1, -1)}
              data-testid="hero-prev-slide"
              aria-label="Previous slide"
              className="h-11 w-11 rounded-full border border-[#F9F6F0]/30 text-[#F9F6F0] grid place-items-center hover:bg-[#F9F6F0] hover:text-[var(--ink)] transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => go(idx + 1, 1)}
              data-testid="hero-next-slide"
              aria-label="Next slide"
              className="h-11 w-11 rounded-full border border-[#F9F6F0]/30 text-[#F9F6F0] grid place-items-center hover:bg-[#F9F6F0] hover:text-[var(--ink)] transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2 ml-2">
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => go(i, i > idx ? 1 : -1)}
                  data-testid={`hero-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-[6px] rounded-full transition-all duration-500 ${
                    i === idx ? "w-10 bg-[var(--gold)]" : "w-5 bg-[#F9F6F0]/30 hover:bg-[#F9F6F0]/60"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#E4DCCB] tabular-nums">
            <span className="text-[var(--gold)]">{String(idx + 1).padStart(2, "0")}</span>
            <span className="mx-2">/</span>
            <span>{String(SLIDES.length).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
